import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Faq from '../components/Faq';
import { Reveal, MaskLine, Eyebrow } from '../components/Reveal';
import { products, spmSpotlight, img, EMAIL, waLink } from '../data/content';
import { pageSeo, productSeo, faqSchema, breadcrumbSchema } from '../data/seo';

const catalogFaqs = [
  { q: 'Which component cleaning machine is right for my part?', a: 'Rotary table and SPM washers suit single-piece flow in machining cells; cabinet washers suit batches and bins; conveyorised through-feed washers suit high-volume lines; ultrasonic, multi-chamber and robotic systems are chosen when precision cleanliness or complex internal geometry is required. Send us your component and we will recommend the right platform.' },
  { q: 'Are Tekwash machines built in stainless steel?', a: 'Yes. Wash chambers, tanks and fixtures are manufactured in SS 304 as standard for corrosion resistance and long service life with aqueous cleaning chemistries.' },
  { q: 'Do you supply industrial washing machines outside Pune and Maharashtra?', a: 'Yes. Tekwash supplies and commissions component cleaning machines across India and exports to customers worldwide, with installation, training and after-sales support.' }
];

function Spotlight() {
  const [imgIdx, setImgIdx] = useState(0);
  return (
    <section className="py-20 lg:py-28" data-testid="products-spotlight">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Featured Machine</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight">{spmSpotlight.name}</h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 items-start">
          <Reveal>
            <div className="clip-notch border border-steelline/80 bg-surface overflow-hidden">
              <img
                key={spmSpotlight.images[imgIdx]}
                src={img(spmSpotlight.images[imgIdx])}
                alt={`${spmSpotlight.name} view ${imgIdx + 1}`}
                className="w-full aspect-[4/3] object-cover"
                data-testid="spotlight-image"
              />
            </div>
            <div className="mt-3 flex gap-3" data-testid="spotlight-thumbs">
              {spmSpotlight.images.map((im, i) => (
                <button
                  key={im + i}
                  onClick={() => setImgIdx(i)}
                  data-testid={`spotlight-thumb-${i}`}
                  className={`w-20 aspect-[4/3] overflow-hidden border transition-colors ${i === imgIdx ? 'border-flame' : 'border-steelline hover:border-fog'}`}
                  aria-label={`View image ${i + 1}`}
                >
                  <img src={img(im)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="border border-steelline/60 divide-y divide-steelline/50 bg-surface/50" data-testid="spotlight-spec-table">
              {spmSpotlight.specs.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-4 px-6 py-4 hover:bg-card/60 transition-colors">
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-fog pt-1">{k}</span>
                  <span className="col-span-2 text-sm text-paper">{v}</span>
                </div>
              ))}
            </div>
            <h3 className="mt-8 font-mono text-xs uppercase tracking-[0.25em] text-flame">Advantages & Features</h3>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {spmSpotlight.features.map((f) => (
                <li key={f} className="flex gap-3 text-sm text-fog items-start">
                  <span className="text-flame mt-[3px]" aria-hidden="true">▸</span>{f}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={waLink('Hello Tekwash Systems, I am interested in the SPM Compact High-Pressure Washer.')} target="_blank" rel="noopener noreferrer" data-testid="spotlight-whatsapp-cta" className="bg-flame text-white font-semibold px-6 py-3.5 clip-notch hover:bg-flamedim transition-colors">
                Request Quote on WhatsApp
              </a>
              <a href={`mailto:${EMAIL}?subject=Quote%20Request%20—%20SPM%20Compact%20High-Pressure%20Washer`} data-testid="spotlight-email-cta" className="border border-steelline text-paper px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:border-flame hover:text-flame transition-colors flex items-center">
                Email For Specs
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function Catalog() {
  return (
    <section className="py-20 lg:py-28 bg-surface/40 border-t border-steelline/60" data-testid="products-catalog">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Full Catalog</Eyebrow>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-paper tracking-tight max-w-2xl">
            Standard platforms. Tailor-made builds.
          </h2>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.1} testid={`product-card-${p.id}`}>
              <article className="group h-full bg-ink border border-steelline/60 hover:border-flame/60 transition-colors duration-300 flex flex-col" data-testid={`product-${p.id}`}>
                <Link to={`/products/${productSeo[p.id].slug}`} className="overflow-hidden block" data-testid={`product-image-link-${p.id}`}>
                  <img src={img(p.images[0])} alt={`${productSeo[p.id].h1} — Tekwash Systems, Pune`} loading="lazy" className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-700" />
                </Link>
                <div className="p-6 flex flex-col grow">
                  <h3 className="font-display font-bold text-xl text-paper">
                    <Link to={`/products/${productSeo[p.id].slug}`} className="hover:text-flame transition-colors" data-testid={`product-title-link-${p.id}`}>{productSeo[p.id].h1}</Link>
                  </h3>
                  <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-skyc">{p.tagline}</p>
                  <p className="mt-4 text-sm text-fog leading-relaxed grow">{p.desc}</p>
                  <ul className="mt-5 space-y-2">
                    {p.bullets.slice(0, 3).map((b) => (
                      <li key={b} className="flex gap-2.5 text-xs text-fog items-start">
                        <span className="text-flame" aria-hidden="true">▸</span>{b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.chips.map((c) => (
                      <span key={c} className="font-mono text-[10px] uppercase tracking-[0.15em] text-fog border border-steelline px-2.5 py-1">{c}</span>
                    ))}
                  </div>
                  <Link to={`/products/${productSeo[p.id].slug}`} data-testid={`product-detail-link-${p.id}`} className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-flame hover:text-paper transition-colors">
                    View Machine Details <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Products() {
  const schemas = [
    faqSchema(catalogFaqs),
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Machines & Products', path: '/products' }]),
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Industrial Component Cleaning Machines by Tekwash Systems',
      itemListElement: products.map((p, i) => ({ '@type': 'ListItem', position: i + 1, name: productSeo[p.id].h1, url: `https://www.tekwashsystems.com/products/${productSeo[p.id].slug}` }))
    }
  ];
  return (
    <>
      <Seo {...pageSeo.products} schemas={schemas} />
      <section className="relative pt-40 pb-20 blueprint-grid overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/40 to-ink" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-5 lg:px-8 relative z-10">
          <Eyebrow className="mb-6 flex items-center gap-3">
            <span className="inline-block h-[1px] w-10 bg-flame" aria-hidden="true" /> Industrial Component Cleaning Machines
          </Eyebrow>
          <h1 className="font-display font-extrabold text-paper leading-[0.98] tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            <MaskLine delay={0.15}>MACHINES BUILT</MaskLine>
            <MaskLine delay={0.3}><span className="text-flame">FOR YOUR PART.</span></MaskLine>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-fog leading-relaxed">
            Rotary table, cabinet, conveyorised, ultrasonic and robotic component cleaning machines — from standard
            parts washers to fully automatic high-pressure robotic systems — plus complete material handling automation.
            Manufactured in Pune, India and supplied worldwide.
          </p>
        </div>
      </section>

      <Spotlight />
      <Catalog />
      <Faq faqs={catalogFaqs} heading="Choosing the right industrial parts washer" testid="products-faq" />
    </>
  );
}
